import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { countries } from 'country-flags-svg';
import { BehaviorSubject, map, Observable, of, shareReplay } from 'rxjs';

import { UriConfig } from '@app/app.config';
import { languageCodesArray } from '@components/_common-modal-components/create-vendor-modal/locale-list';
import { LocalesEntityEnum } from '@enums/LocalesEntityEnum';
import { LocalesTypeEnum } from '@enums/LocalesTypeEnum';
import { TenantsEnum } from '@enums/TenantsEnum';
import { CreateLocaleItemModel } from '@models/CreateLocaleItemModel';
import { CreateGoogleTranslateModel, GoogleTranslateModel } from '@models/GoogleTranslateModel';
import { LocaleItemModel } from '@models/LocaleItemModel';
import { LocaleModel } from '@models/LocaleModel';
import { LocalesItemRequestModel, LocalesRequestModel } from '@models/LocalesItemRequestModel';
import { CommonLocaleItem, LocaleItem, LocalesResponseModel } from '@models/LocalesResponseModel';
import { ProfileService } from '@services/profile.service';
import { QuestionsBankService } from '@services/questions-bank.service';

import { TextService } from './text.service';

const CountryLanguage = require('@ladjs/country-language');
const langs = require('langs');

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  protected http = inject(HttpClient);
  private uriConfig = inject(UriConfig);
  private textService = inject(TextService);
  private fb = inject(NonNullableFormBuilder);
  private questionBankService = inject(QuestionsBankService);
  private profileService = inject(ProfileService);

  detectedLanguage: { lang: string; questionId?: number; entity?: LocalesEntityEnum }[];

  fullLocaleData$: Observable<LocaleModel[]>;

  needUpdateLocaleList$ = new BehaviorSubject(true);

  vendorLocales$: Observable<LocaleItemModel[]>;

  getRoundLocales(roundId: number): Observable<LocalesResponseModel> {
    return this.http.get<LocalesResponseModel>(this.uriConfig.localizations + '/' + roundId).pipe(
      map(round => {
        const isStreak = this.profileService.currentUser$.value?.tenantType === TenantsEnum.STREAK;
        if (!isStreak && round.questions) {
          round.questions = round.questions.map(question => {
            if (!question.answers) {
              return question;
            } else {
              question.answers = question.answers.filter(
                answer => !this.questionBankService.externalAnswerOptions.includes(answer.text)
              );
              return question;
            }
          });
        }
        return round;
      })
    );
  }

  getMarketLocales(marketId: number): Observable<LocalesResponseModel> {
    return this.http.get<LocalesResponseModel>(`${this.uriConfig.markets}/${marketId}/localizations`);
  }

  bulkSaveLocale(body: LocalesRequestModel): Observable<LocaleItem[]> {
    return this.http.post<LocaleItem[]>(this.uriConfig.localizations, body);
  }

  bulkEditLocale(body: LocalesRequestModel): Observable<LocaleItem[]> {
    return this.http.patch<LocaleItem[]>(this.uriConfig.localizations, body);
  }

  getGoogleTranslation(body: { items: CreateGoogleTranslateModel[] }): Observable<{ data: GoogleTranslateModel[] }> {
    return this.http.post<{ data: GoogleTranslateModel[] }>(this.uriConfig.localizations + '/translate', body);
  }

  getVendorLocales(): Observable<LocaleItemModel[]> {
    return this.http.get<LocaleItemModel[]>(this.uriConfig.locales);
  }

  createVendorLocale(vendorLocaleBody: CreateLocaleItemModel): Observable<void> {
    return this.http.post<void>(this.uriConfig.locales, vendorLocaleBody);
  }

  editVendorLocale(vendorLocaleBody: CreateLocaleItemModel, localeId: number): Observable<void> {
    return this.http.patch<void>(this.uriConfig.locales + '/' + localeId, vendorLocaleBody);
  }

  deleteVendorLocale(localeId: number): Observable<void> {
    return this.http.delete<void>(this.uriConfig.locales + '/' + localeId);
  }

  fetchVendorLocale(): void {
    this.vendorLocales$ = this.getVendorLocales().pipe(shareReplay(1));
  }

  getEntityValue(entity: string): string {
    switch (entity) {
      case LocalesEntityEnum.SPORT_EVENT:
        return `${this.textService.capitalizeFirstLetter(entity).split('_').join(' ')} name`;
      case LocalesEntityEnum.BRAND_URL:
        return `Brand Upsell Source Url`;
      case LocalesEntityEnum.LEADERBOARD_NAME:
        return `Leaderboard Name`;
      case LocalesEntityEnum.CHALLENGE_NAME:
        return `Challenge title`;
      case LocalesEntityEnum.CHALLENGE_TOOLTIP_TEXT:
        return `Challenge Tooltip Text`;
      case LocalesEntityEnum.CHALLENGE_DESCRIPTION:
        return `Challenge Description`;
      case LocalesEntityEnum.CHALLENGE_PRIZE_LABEL:
        return `Prize label text`;
      case LocalesEntityEnum.TIER_NAME:
        return `Tier Name`;
      case LocalesEntityEnum.UPSELL_NAME:
        return `Upsell Name`;
      case LocalesEntityEnum.UPSELL_TITLE:
        return `Upsell Title`;
      case LocalesEntityEnum.UPSELL_DESCRIPTION:
        return `Upsell Description`;
      case LocalesEntityEnum.UPSELL_BUTTON:
        return `Upsell Button Name`;
      case LocalesEntityEnum.UPSELL_WEB_LINK:
        return `Upsell Web Link`;
      case LocalesEntityEnum.UPSELL_MOBILE_LINK:
        return `Upsell App Link`;
      case LocalesEntityEnum.ROUND:
        return `${this.textService.capitalizeFirstLetter(entity)} header`;
      case LocalesEntityEnum.STREAK:
        return `${this.textService.capitalizeFirstLetter(entity)} name`;
      case LocalesEntityEnum.TEAM:
        return `${this.textService.capitalizeFirstLetter(entity)} name`;
      case LocalesEntityEnum.PRIZE_TEXT:
        return 'Game name';
      case LocalesEntityEnum.BONUS:
        return 'Bonus';
      case LocalesEntityEnum.ANSWER:
      case LocalesEntityEnum.QUESTION:
      case LocalesEntityEnum.PRIZE:
      case LocalesEntityEnum.LEADERBOARD:
        return `${this.textService.capitalizeFirstLetter(entity)} text`;
      case LocalesEntityEnum.DESCRIPTION:
        return `Round Description`;
      case LocalesEntityEnum.MARKET:
        return `Market Name`;
      case LocalesEntityEnum.MARKET_EXPLANATION:
        return `Market Explanation`;
      case LocalesEntityEnum.MARKET_FACT:
        return `Market Fact`;
      case LocalesEntityEnum.CORNER_LABEL:
        return `Corner Label`;
    }
  }

  getLocaleValues(localization: CommonLocaleItem[]) {
    if (localization && localization.length) {
      return localization.reduce((acc, item) => {
        const { id, text } = item;
        acc[item.locale.i18n] = { text, id };
        return acc;
      }, {});
    }
    return null;
  }

  createGoogleTranslateBody(entityList, locales) {
    return locales.reduce((acc, target) => {
      acc.push({
        target: target.i18n,
        q: entityList.map(entity => entity.text || entity.name)
      });
      return acc;
    }, []);
  }

  prepareLocaleObject(entityLocaleSetting, entity, vendorLocale, maxLenght?) {
    const itemFormGroup = this.fb.group({});
    vendorLocale.forEach(localeItem => {
      const values = this.getLocaleValues(entityLocaleSetting.localizations);
      itemFormGroup.addControl(
        localeItem.i18n,
        this.fb.control(values && values[localeItem.i18n] ? values[localeItem.i18n].text : '')
      );
      if (maxLenght) {
        itemFormGroup.get(localeItem.i18n).addValidators([Validators.maxLength(maxLenght)]);
      }
      itemFormGroup.addControl(
        `${localeItem.i18n}Id`,
        this.fb.control(values && values[localeItem.i18n] ? values[localeItem.i18n].id : '')
      );
    });

    if (entityLocaleSetting.leaderboardId) {
      itemFormGroup.addControl('id', this.fb.control(entityLocaleSetting.leaderboardId));
    }

    if (entityLocaleSetting.challengeId) {
      itemFormGroup.addControl('id', this.fb.control(entityLocaleSetting.challengeId));
    }

    if (entityLocaleSetting.tierId) {
      itemFormGroup.addControl('id', this.fb.control(entityLocaleSetting.tierId));
    }

    if (entityLocaleSetting.roundDescriptionId) {
      itemFormGroup.addControl('id', this.fb.control(entityLocaleSetting.roundDescriptionId));
    }

    if (entityLocaleSetting.marketId) {
      itemFormGroup.addControl('id', this.fb.control(entityLocaleSetting.marketId));
    }

    if (entityLocaleSetting.id) {
      itemFormGroup.addControl('id', this.fb.control(entityLocaleSetting.id));
    }

    if (!this.isLinkEntity(entity) && this.detectedLanguage && this.detectedLanguage.length) {
      let targetKey = 'id';
      if (entityLocaleSetting.leaderboardId) targetKey = 'leaderboardId';
      if (entityLocaleSetting.roundDescriptionId) targetKey = 'roundDescriptionId';
      const detectedLocale = this.detectedLanguage.find(lang => {
        if (lang.questionId && !lang.entity) return lang.questionId === entityLocaleSetting[targetKey];
        if (!lang.questionId && lang.entity) return lang.entity === entity;
      });
      const detectedLanguage = languageCodesArray.find(
        localeItem => localeItem.localeCode === detectedLocale?.lang
      )?.country;
      itemFormGroup.addControl('detected', this.fb.control(detectedLanguage));
    }

    itemFormGroup.addControl('text', this.fb.control(entityLocaleSetting.text || entityLocaleSetting.name));
    const entityValue = this.getEntityValue(entity);
    itemFormGroup.addControl('entityValue', this.fb.control(entityValue));
    itemFormGroup.addControl('entity', this.fb.control(entity));
    itemFormGroup.addControl('actions', this.fb.control(true));

    return itemFormGroup;
  }

  isLinkEntity(entity: LocalesEntityEnum): boolean {
    return (
      entity === LocalesEntityEnum.BRAND_URL ||
      entity === LocalesEntityEnum.UPSELL_WEB_LINK ||
      entity === LocalesEntityEnum.UPSELL_MOBILE_LINK
    );
  }

  createLocaleObjectData(): LocaleModel[] {
    const allLaguage = langs.all();

    const fullLocaleData = languageCodesArray.map(({ localeCode, country, simplify }) => {
      let countryName = country;
      //If in country list use clarification in brackets, and this locale, marked as simplify
      //(there approved languages which can be simplify) use country name without clarification
      //to search flag

      if (simplify) {
        countryName = country.split(' ')[0];
      }

      const countryIso = allLaguage.find(c => c.name.toLowerCase() === countryName.toLowerCase());
      let flag: string;

      //Need hardcode only for english, becouse there we need USA flag
      if (localeCode === 'en') {
        flag = countries.find(countryItem => countryItem.iso3.toLowerCase() === 'usa')?.flag;
      } else {
        flag = countries.find(countryItem => countryItem.demonym.toLowerCase() === countryName.toLowerCase())?.flag;
      }

      if (!flag && countryIso) {
        flag = countries.find(countryItem => countryItem.iso3.toLowerCase() === countryIso['2B'].toLowerCase())?.flag;
      }

      if (!flag) {
        CountryLanguage.getLanguageCountries(localeCode, (err, languages) => {
          //Need only if language use in one country
          if (languages && languages.length && languages.length === 1) {
            const indic = languages[0]['code_3'];
            flag = countries.find(countryItem => countryItem.iso3.toLowerCase() === indic.toLowerCase())?.flag;
          }
        });
      }
      return new LocaleModel(localeCode, country, undefined, flag);
    });
    return fullLocaleData;
  }

  prepareFullLocaleData(): void {
    this.fullLocaleData$ = of(this.createLocaleObjectData());
  }

  prepareVendorLocales(vendorLocale: LocaleItemModel[]) {
    return vendorLocale.map(localeData => {
      const countryName = languageCodesArray.find(country => country.localeCode === localeData.i18n).country;
      const { id, i18n } = localeData;
      return {
        locale: i18n,
        countryName,
        id
      };
    });
  }

  getLocalizationTableColumn(vendorLocales, additionColumns: string[] = []): string[] {
    const locales = vendorLocales.map(localeItem => localeItem.locale);
    return ['entityValue', 'text', ...locales, ...additionColumns];
  }

  createLocaleRequestObject(itemFormGroup, vendorLocales) {
    const items = [];
    const editedItems = [];
    const { id } = itemFormGroup.value;

    vendorLocales.forEach(locale => {
      const currentFormControl = itemFormGroup.get(locale.locale);
      let text = itemFormGroup.value[locale.locale];
      const localeId = locale.id;
      if (!text && !currentFormControl.dirty) return;
      if (this.textService.hasSpacesAtStartOrEnd(text)) {
        text = text.trim();
        itemFormGroup.patchValue({
          [locale.locale]: text
        });
      }
      const requestBody: LocalesItemRequestModel = { text, localeId };
      if (itemFormGroup.value[`${locale.locale}Id`]) {
        requestBody.id = itemFormGroup.value[`${locale.locale}Id`];
        editedItems.push(requestBody);
      } else {
        switch (itemFormGroup.value.entity) {
          case LocalesEntityEnum.ANSWER:
            requestBody.answerId = id;
            break;
          case LocalesEntityEnum.QUESTION:
            requestBody.questionId = id;
            break;
          case LocalesEntityEnum.SPORT_EVENT:
            requestBody.sportEventId = id;
            break;
          case LocalesEntityEnum.TEAM:
            requestBody.teamId = id;
            break;
          case LocalesEntityEnum.CHALLENGE_NAME:
          case LocalesEntityEnum.CHALLENGE_DESCRIPTION:
          case LocalesEntityEnum.CHALLENGE_PRIZE_LABEL:
          case LocalesEntityEnum.CHALLENGE_TOOLTIP_TEXT:
            requestBody.challengeId = id;
            break;
          case LocalesEntityEnum.BRAND_URL:
            requestBody.brandId = id;
            break;
          case LocalesEntityEnum.PRIZE:
            requestBody.prizeLabelId = id;
            break;
          case LocalesEntityEnum.TIER_NAME:
            requestBody.leaderboardTierId = id;
            break;
          case LocalesEntityEnum.LEADERBOARD:
          case LocalesEntityEnum.LEADERBOARD_NAME:
            requestBody.leaderboardId = id;
            break;
          case LocalesEntityEnum.DESCRIPTION:
            requestBody.roundDescriptionId = id;
            break;
          case LocalesEntityEnum.MARKET:
          case LocalesEntityEnum.MARKET_EXPLANATION:
          case LocalesEntityEnum.MARKET_FACT:
            requestBody.marketId = id;
            break;
        }
        items.push(requestBody);
      }
    });
    return { items, editedItems };
  }

  prepareLocalesList(vendorLocale) {
    return vendorLocale.map(localeData => {
      const country = languageCodesArray.find(country => country.localeCode === localeData.i18n).country;
      const { id, i18n } = localeData;
      return {
        i18n,
        country,
        id
      };
    });
  }

  mapTranslationsToCmsContentBody(
    eventName: string,
    dataArray: { [p: string]: string }[],
    translationsArray: GoogleTranslateModel[]
  ) {
    dataArray.forEach(item => {
      translationsArray.forEach((translation: GoogleTranslateModel) => {
        item['Text value'] = eventName;

        const locale = translation.locale;
        const translatedText = translation.translations[0]?.translatedText || null;

        const labelKey = `Text value_${locale}`;

        if (translatedText !== null) {
          item[labelKey] = translatedText;
        }
      });

      Object.keys(item).forEach(key => {
        if (item[key] === null && key.includes('Text value')) {
          delete item[key];
        }
      });
    });
    return dataArray;
  }

  hasLocalizations(obj) {
    if (obj && typeof obj === 'object') {
      if ('localizations' in obj) {
        return true;
      }
      for (const key in obj) {
        if (this.hasLocalizations(obj[key])) {
          return true;
        }
      }
    }
    return false;
  }

  createLocaleSaveBody(
    items: LocalesItemRequestModel[],
    entity: LocalesTypeEnum,
    roundId: number
  ): LocalesRequestModel {
    const localeRequestBody: LocalesRequestModel = {
      items
    };

    if (entity === LocalesTypeEnum.UPSELL) {
      localeRequestBody.fromUpsell = true;
    }

    if (entity === LocalesTypeEnum.MARKET) {
      localeRequestBody.fromMarket = true;
    }

    if (this.isRoundType(entity)) {
      localeRequestBody.roundId = roundId;
    }

    return localeRequestBody;
  }

  isRoundType(entity: LocalesTypeEnum): boolean {
    return entity === LocalesTypeEnum.ROUND || entity === LocalesTypeEnum.GLOBAL_STREAK_ROUND;
  }
}
